import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { environment } from '@env/fe';
import { Store } from '@ngrx/store';
import { AuthService, SystemsService } from '@twaice-fe/frontend/shared/services';
import { actions } from '@twaice-fe/frontend/shared/store';
import { ENERGY_BASE_ROUTE, MOBILITY_BASE_ROUTE, MODEL_LIBRARY_BASE_ROUTE } from '@twaice-fe/shared/constants';
import { CustomerType, parseProductPackages } from '@twaice-fe/shared/models';
import { distinctUntilChanged } from 'rxjs';

const {
  configsAction: { loadConfigSuccess },
} = actions;

@Component({
  selector: 'twaice-fe-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  release = environment.release;
  featureFlags = {};

  constructor(
    private router: Router,
    private systemService: SystemsService,
    private authService: AuthService,
    protected store: Store,
    private destroy$: DestroyRef
  ) {}

  ngOnInit(): void {
    // route guards gate on the product package, so the config has to be reduced before
    // navigation starts — matching a route without it lets gated pages through
    this.initSystemConfig();
    this.startNavigationWithoutConfig();
  }

  getBaseUrl(customerType: CustomerType): string {
    switch (customerType) {
      case CustomerType.ENERGY:
        return ENERGY_BASE_ROUTE;
      case CustomerType.MOBILITY:
        return MOBILITY_BASE_ROUTE;
      case CustomerType.MODELS:
        return MODEL_LIBRARY_BASE_ROUTE;
    }

    console.error('Could not match config to customer type!');

    return '';
  }

  private initSystemConfig() {
    this.systemService
      .getSystemUiConfiguration()
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroy$))
      .subscribe({
        next: (config) => {
          if (!config) return;

          const featureFlagList = config.featureFlags?.filter(Boolean);
          featureFlagList.forEach((flag) => (this.featureFlags[flag] = true));

          this.store.dispatch(
            loadConfigSuccess({
              featureFlags: featureFlagList,
              solutions: config.solutions,
              customerType: config.customerType,
              customerBk: config.customerBk,
              baseUrl: this.getBaseUrl(config.customerType),
              productPackages: parseProductPackages(config.productPackages),
            })
          );

          this.router.initialNavigation();
        },
        error: () => this.router.initialNavigation(),
      });
  }

  /**
   * Starts navigation for the cases where no config is coming: a logged out user has none to
   * fetch, and waiting on it would strand them on a blank page instead of the login one.
   *
   * `initialNavigation()` only acts on the first call, so whichever case gets there first wins.
   */
  private startNavigationWithoutConfig() {
    this.authService.waitForAuthComplete().then(() => {
      if (!this.authService.isLoggedIn()) this.router.initialNavigation();
    });
  }
}
